import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select/SelectInput";
import Spinner from "components/Spinner";

import {
  MasterPropertyCategories,
  MasterPropertyType,
} from "constants/constant";
import masterPropertyApi from "middlewares/MasterProperty/masterPropertyListApi";
import { MasterPropertyModel } from "object-models/shared/master-property.model";
import numericValidator from "services/numericValidator";
import CreateLocationDesign from "./CreateUpdateLocationDesign";
import NotificationHelper from "services/notification-helper";
import { useAppState } from "context/appState.context";
import { BusinessUnitModel } from "object-models/BusinessUnitModel";
import { LocationModel } from "object-models/location-model";
import { HistoricalEmissionData } from "object-models/HistoricalEmissionData";
import {
  _addLocation,
  _getLocation,
  _IsLocationExist,
  _updateLocation,
} from "middlewares/LocationApi/location";

interface CreateLocationProps {
  closeDialog: () => void;
  isEdit?: boolean;
  readonly?: boolean;
  showSaveButton?: boolean;
  onLocationCreated?: (location: LocationModel) => void;
}

/**
 * Component for creating or updating a location.
 */

export default function CreateUpdateLocation(
  props: Readonly<CreateLocationProps>
) {
  const defaultLocation: LocationModel = {
    locationId: 0,
    orgId: 0,
    locationName: "",
    currency: "",
    noOfEmployees: "",
    isActive: true,
    scope1GHGEmission: undefined,
    scope2GHGEmission: undefined,
    scope3GHGEmission: undefined,
    purchasedGoodsAndServices: undefined,
    capitalGoods: undefined,
    fuelandEnergyRelatedActivities: undefined,
    upstreamTransportationAndDistribution: undefined,
    wasteGeneratedinOperations: undefined,
    businessTravel: undefined,
    employeeCommuting: undefined,
    upstreamLeasedAssets: undefined,
    downstreamTransportationAndDistribution: undefined,
    processingofSoldProducts: undefined,
    useofSoldProducts: undefined,
    endofLifeTreatmentofSoldProducts: undefined,
    downstreamLeasedAssets: undefined,
    franchises: undefined,
    investments: undefined,
    otherUpstream: undefined,
    otherDownstream: undefined,
    businessUnits: [],
    historicalEmissionDatas: [],
  };

  const businessUnitList = ["1", "2", "3", "4", "5"];

  const { LocationId: routeLocationId } = useParams();
  // Check localStorage if LocationId is not in route params (e.g., when opened in dialog)
  const LocationId = routeLocationId ?? localStorage.getItem("LocationId");
  const orgId = localStorage.getItem("orgId");

  const { setNotificationConfig } = useAppState();

  const [draftLocation, setDraftLocation] = useState({
    ...defaultLocation,
    orgId: orgId ? parseInt(orgId) : 0,
  });


  const [industryList, setIndustryList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [annualRevenueRangeList, setAnnualRevenueRangeList] = useState(
    [] as Array<MasterPropertyModel>
  );
  const [loading, setLoading] = useState(false);
  const [noOfEmployeesRangeList, setNoOfEmployeesRangeList] = useState(
    [] as Array<MasterPropertyModel>
  );

  // Refs to prevent duplicate API calls (React StrictMode double-mounts in dev)
  const isLocationFetched = useRef(false);
  const isStaticListsFetched = useRef(false);

  /**
   * Handles changes in form fields.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent} event - The change event object.
   */
  function fieldChangeHandler(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) {
    const numericCheckColumns = [
      "scope1GHGEmission",
      "scope2GHGEmission",
      "scope3GHGEmission",
      "purchasedGoodsAndServices",
      "capitalGoods",
      "fuelandEnergyRelatedActivities",
      "upstreamTransportationAndDistribution",
      "wasteGeneratedinOperations",
      "businessTravel",
      "employeeCommuting",
      "upstreamLeasedAssets",
      "downstreamTransportationAndDistribution",
      "processingofSoldProducts",
      "useofSoldProducts",
      "endofLifeTreatmentofSoldProducts",
      "downstreamLeasedAssets",
      "franchises",
      "investments",
      "otherUpstream",
      "otherDownstream",
    ];

    setDraftLocation((prevState) => ({
      ...prevState,
      [event.target.name]: numericCheckColumns.includes(event.target.name)
        ? numericValidator
            .allowNumericValue(event.target.value)
            .toLocaleString("en-US")
        : event.target.value,
    }));
  }

  /**
   * Handles the addition of business units.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent} event - The change event object.
   */
  function AddBusinessUnit(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) {
    const noOfBusinessUnit = parseInt(event.target.value);
    setDraftLocation((prevState) => {
      const businessUnit = [...(prevState.businessUnits ?? [])];
      const currentCount = businessUnit.length;
      if (currentCount > noOfBusinessUnit) {
        businessUnit.splice(noOfBusinessUnit, currentCount);
      } else {
        for (let i = currentCount; i < noOfBusinessUnit; i++) {
          const businessUnitObject: BusinessUnitModel = {
            id: 0,
            locationID: prevState.locationId,
            name: "",
          };
          businessUnit.push(businessUnitObject);
        }
      }
      return {
        ...prevState,
        businessUnits: businessUnit,
      };
    });
  }

  /**
   * Updates the business unit at the specified index.
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent} event - The change event object.
   * @param {number} index - The index of the business unit to update.
   */
  function updateBusinessUnit(
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent,
    index: number
  ) {
    setDraftLocation((prevState) => {
      if (!prevState.businessUnits) return prevState;

      const updatedBusinessUnits = [...prevState.businessUnits];
      updatedBusinessUnits[index] = {
        ...updatedBusinessUnits[index],
        name: event.target.value,
      };
      return {
        ...prevState,
        businessUnits: updatedBusinessUnits,
      };
    });
  }

  /**
   * Initializes historical emission data for a range of years.
   * @param {number} startYear - The start year for historical emissions.
   * @param {number} endYear - The end year for historical emissions.
   */
  function initializeHistoricalEmissionData(startYear: number, endYear: number) {
    setDraftLocation((prevState) => {
      const historicalData: HistoricalEmissionData[] = [];
      for (let year = startYear; year <= endYear; year++) {
        const existingData = prevState.historicalEmissionDatas?.find(
          (data) => data.emissionYear === year
        );
        
        if (existingData) {
          historicalData.push(existingData);
        } else {
          historicalData.push({
            id: 0,
            emissionYear: year,
            locationId: prevState.locationId,
            scope1GHGEmission: null,
            scope2GHGEmission: null,
            scope3GHGEmission: null,
            purchasedGoodsAndServices: null,
            capitalGoods: null,
            fuelandEnergyRelatedActivities: null,
            upstreamTransportationAndDistribution: null,
            wasteGeneratedinOperations: null,
            businessTravel: null,
            employeeCommuting: null,
            upstreamLeasedAssets: null,
            downstreamTransportationAndDistribution: null,
            processingofSoldProducts: null,
            useofSoldProducts: null,
            endofLifeTreatmentofSoldProducts: null,
            downstreamLeasedAssets: null,
            franchises: null,
            investments: null,
            otherUpstream: null,
            otherDownstream: null,
          });
        }
      }
      return {
        ...prevState,
        historicalEmissionData: historicalData,
      };
    });
  }

  /**
   * Updates a specific field in historical emission data.
   * @param {number} year - The emission year to update.
   * @param {string} fieldName - The field name to update.
   * @param {string} value - The new value.
   */
  function updateHistoricalEmissionData(
    year: number,
    fieldName: string,
    value: string
  ) {
    const scope3Fields = [
      "purchasedGoodsAndServices",
      "capitalGoods",
      "fuelandEnergyRelatedActivities",
      "upstreamTransportationAndDistribution",
      "wasteGeneratedinOperations",
      "businessTravel",
      "employeeCommuting",
      "upstreamLeasedAssets",
      "downstreamTransportationAndDistribution",
      "processingofSoldProducts",
      "useofSoldProducts",
      "endofLifeTreatmentofSoldProducts",
      "downstreamLeasedAssets",
      "franchises",
      "investments",
      "otherUpstream",
      "otherDownstream",
    ];

    setDraftLocation((prevState) => {
      if (!prevState.historicalEmissionDatas) return prevState;

      const updatedData = prevState.historicalEmissionDatas.map((data) => {
        if (data.emissionYear === year) {
          const numericValue = numericValidator.allowNumericValue(value);
          const numberValue = numericValue === '' || numericValue === undefined ? null : parseFloat(numericValue.toString());
          const updatedRecord = {
            ...data,
            [fieldName]: (numberValue !== null && !isNaN(numberValue) && numberValue >= 0) ? numberValue : null,
          };

          // Auto-calculate scope3GHGEmission if a Scope 3 category field was updated
          if (scope3Fields.includes(fieldName)) {
            const scope3Total = scope3Fields.reduce((sum, field) => {
              let fieldValue;
              if (field === fieldName) {
                if (numberValue !== null && !isNaN(numberValue) && numberValue >= 0) {
                  fieldValue = numberValue;
                } else {
                  fieldValue = 0;
                }
              } else {
                const value = updatedRecord[field as keyof typeof updatedRecord] as number;
                fieldValue = value ?? 0;
              }
              return sum + fieldValue;
            }, 0);
            updatedRecord.scope3GHGEmission = scope3Total > 0 ? scope3Total : null;
          }

          return updatedRecord;
        }
        return data;
      });

      return {
        ...prevState,
        historicalEmissionData: updatedData,
      };
    });
  }

  useEffect(() => {
    const scope3GHGEmissionFields = [
      "purchasedGoodsAndServices",
      "capitalGoods",
      "fuelandEnergyRelatedActivities",
      "upstreamTransportationAndDistribution",
      "wasteGeneratedinOperations",
      "businessTravel",
      "employeeCommuting",
      "upstreamLeasedAssets",
      "downstreamTransportationAndDistribution",
      "processingofSoldProducts",
      "useofSoldProducts",
      "endofLifeTreatmentofSoldProducts",
      "downstreamLeasedAssets",
      "franchises",
      "investments",
      "otherUpstream",
      "otherDownstream",
    ];
    const sum = scope3GHGEmissionFields.reduce((total, propName) => {
      const propValue = draftLocation[propName as keyof LocationModel];
      const stringValue =
        typeof propValue === "string" || typeof propValue === "number"
          ? String(propValue).trim()
          : "";
      return parseFloat(
        (
          total + (stringValue.length > 0 ? parseFloat(stringValue) ?? 0 : 0)
        ).toFixed(2)
      );
    }, 0);
    setDraftLocation((prevState) => ({
      ...prevState,
      scope3GHGEmission: sum > 0.0 ? sum : 0,
    }));
  }, [
    draftLocation.purchasedGoodsAndServices,
    draftLocation.capitalGoods,
    draftLocation.fuelandEnergyRelatedActivities,
    draftLocation.upstreamTransportationAndDistribution,
    draftLocation.wasteGeneratedinOperations,
    draftLocation.businessTravel,
    draftLocation.employeeCommuting,
    draftLocation.upstreamLeasedAssets,
    draftLocation.downstreamTransportationAndDistribution,
    draftLocation.processingofSoldProducts,
    draftLocation.useofSoldProducts,
    draftLocation.endofLifeTreatmentofSoldProducts,
    draftLocation.downstreamLeasedAssets,
    draftLocation.franchises,
    draftLocation.investments,
    draftLocation.otherUpstream,
    draftLocation.otherDownstream,
  ]);

  /**
   * Fetches location details if the component is in edit mode.
   */
  function getLocationDetails() {
    setLoading(true);
    if (LocationId !== undefined && LocationId !== null && LocationId?.length > 0) {
      _getLocation(+LocationId)
        .then((res) => {
          // Convert invalid noOfEmployees values (0, "0", null, undefined) to empty string
          // to avoid MUI Select "out-of-range value" warning
          const noOfEmployeesValue = res.noOfEmployees;
          const sanitizedNoOfEmployees = 
            noOfEmployeesValue && String(noOfEmployeesValue) !== "0"
              ? String(noOfEmployeesValue)
              : "";
          setDraftLocation({
            ...res,
            noOfEmployees: sanitizedNoOfEmployees,
          });
          setLoading(false);
        })
        .catch((err) => {});
    }
  }

  /**
   * Checks if the location with the given name already exists.
   * @param {string} locationName - The name of the location to check.
   * @returns {Promise<boolean>} - Returns true if the location exists, otherwise false.
   */
  async function isLocationExist(locationName: string) {
    if (locationName === draftLocation.locationName) {
      return false;
    }
    if (locationName !== "") {
      try {
        const exist = await _IsLocationExist(locationName);
        return exist;
      } catch (err) {
        console.error("Error checking location existence:", err);
        return false;
      }
    } else {
      return false;
    }
  }

  function createPayload(location: LocationModel) {
    const defaultToZero = (value: number | undefined | null | string) =>
      value === "" || value === null || value === undefined ? 0 : Number(value);


    // Get emission values from the historical data for the selected base year
    // Convert baseYear to number to ensure type match with emissionYear
    const baseYearNum = Number(location.baseYear);
    const baseYearData = location.historicalEmissionDatas?.find(
      (data) => data.emissionYear === baseYearNum
    );

    return {
      ...location,
      // Use base year historical data values if available, otherwise fallback to location values
      scope1GHGEmission: defaultToZero(baseYearData?.scope1GHGEmission ?? 0),
      scope2GHGEmission: defaultToZero(baseYearData?.scope2GHGEmission ?? 0),
      scope3GHGEmission: defaultToZero(baseYearData?.scope3GHGEmission ?? 0),
      purchasedGoodsAndServices: defaultToZero(
        baseYearData?.purchasedGoodsAndServices ?? 0
      ),
      capitalGoods: defaultToZero(baseYearData?.capitalGoods ?? 0),
      fuelandEnergyRelatedActivities: defaultToZero(
        baseYearData?.fuelandEnergyRelatedActivities ?? 0
      ),
      upstreamTransportationAndDistribution: defaultToZero(
        baseYearData?.upstreamTransportationAndDistribution ?? 0
      ),
      wasteGeneratedinOperations: defaultToZero(
        baseYearData?.wasteGeneratedinOperations ?? 0
      ),
      businessTravel: defaultToZero(baseYearData?.businessTravel ?? 0),
      employeeCommuting: defaultToZero(baseYearData?.employeeCommuting ?? 0),
      upstreamLeasedAssets: defaultToZero(baseYearData?.upstreamLeasedAssets ?? 0),
      downstreamTransportationAndDistribution: defaultToZero(
        baseYearData?.downstreamTransportationAndDistribution ?? 0
      ),
      processingofSoldProducts: defaultToZero(
        baseYearData?.processingofSoldProducts ?? 0
      ),
      endofLifeTreatmentofSoldProducts: defaultToZero(
        baseYearData?.endofLifeTreatmentofSoldProducts ?? 0
      ),
      downstreamLeasedAssets: defaultToZero(
        baseYearData?.downstreamLeasedAssets ?? 0
      ),
      useofSoldProducts: defaultToZero(baseYearData?.useofSoldProducts ?? 0),
      franchises: defaultToZero(baseYearData?.franchises ?? 0),
      investments: defaultToZero(baseYearData?.investments ?? 0),
      otherUpstream: defaultToZero(baseYearData?.otherUpstream ?? 0),
      otherDownstream: defaultToZero(baseYearData?.otherDownstream ?? 0),
    };
  }

  /**
   * Creates a new location.
   */
  function create(location: LocationModel) {
    setLoading(true);
    const payload = createPayload(location);
    _addLocation(payload)
      .then((res) => {
        if (res) {
          setLoading(false);
          props.onLocationCreated?.(res);
          props.closeDialog();
          const notification = NotificationHelper.successNotification(
            `Location ${res.locationName} created successfully`
          );
          setNotificationConfig(notification);
        }
      })
      .catch((err) => {
        setLoading(false);
      });
  }

  /**
   * Updates the existing location.
   */
  function update(location: LocationModel) {
    setLoading(true);
    const payload = createPayload(location);
    _updateLocation(payload)
      .then((res) => {
        if (res) {
          // Reflect the saved values immediately since this component isn't remounted on save
          setDraftLocation((prevState) => ({
            ...prevState,
            ...res,
          }));
          setLoading(false);
          props.closeDialog();
          const notification = NotificationHelper.successNotification(
            `Location ${res.locationName} saved successfully`
          );
          setNotificationConfig(notification);
        }
      })
      .catch((err) => {
        setLoading(false);
      });
  }

  function onCreateSave(location: LocationModel) {
    createPayload(location);
    create(location);
  }

  function onUpdateSave(location: LocationModel) {
    createPayload(location);
    update(location);
  }

  /**
   * Fetches static lists required for creating an location.
   */
  async function getCreateLocationStaticLists() {
    setLoading(true);
    masterPropertyApi
      .getMasterPropertyListByCategory(MasterPropertyCategories.Location)
      .then((res) => {
        if (res.length > 0) {
          setIndustryList(
            res.filter(
              (prop) => prop.type === MasterPropertyType.Location.Industry
            )
          );

          setNoOfEmployeesRangeList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.Location.NoOfEmployeesRange
            )
          );
          setAnnualRevenueRangeList(
            res.filter(
              (prop) =>
                prop.type === MasterPropertyType.Location.AnnualRevenueRange
            )
          );
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("error");
        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }
  useEffect(() => {
    if (!isStaticListsFetched.current) {
      isStaticListsFetched.current = true;
      getCreateLocationStaticLists();
    }
  }, []);
  useEffect(() => {
    if (props.isEdit !== undefined && props.isEdit === true && !isLocationFetched.current) {
      isLocationFetched.current = true;
      getLocationDetails();
    }
  }, [props.isEdit]);

  // Debug: Log when historicalEmissionData changes
  useEffect(() => {
  }, [draftLocation.historicalEmissionDatas]);

  return (
    <React.Fragment>
      <Spinner size={80} data-testid="spinner" open={loading} />
      <CreateLocationDesign
        location={draftLocation}
        fieldChangeHandler={fieldChangeHandler}
        onSave={props.isEdit ? onUpdateSave : onCreateSave}
        industryList={industryList}
        NoOfEmployeesRangeList={noOfEmployeesRangeList}
        annualRevenueRangeList={annualRevenueRangeList}
        isLocationExist={isLocationExist}
        businessUnitList={businessUnitList}
        AddBusinessUnit={AddBusinessUnit}
        UpdateBusinessUnit={updateBusinessUnit}
        initializeHistoricalEmissionData={initializeHistoricalEmissionData}
        updateHistoricalEmissionData={updateHistoricalEmissionData}
        isEdit={props.isEdit}
        readonly={props.readonly}
        showSaveButton={props.showSaveButton}
      ></CreateLocationDesign>
    </React.Fragment>
  );
}
